import * as React from 'react'
import * as cn from 'classnames'
import * as styles from './Hover.module.scss'
import {
    WatchlistAction,
    DVString,
    TrailerFragment,
    WPSClient,
    withArgs,
    Args,
    StorefrontItemLive,
    ItemPrerolls,
    EnrichItemMetadataOutput,
    HideThisOutput,
    HideThisAction,
    HideThisTag,
    safeBool,
    MaturityRatingProps as LinkableMaturityRatingProps
} from 'DVWebNodeUtils'
import WatchlistStatus from './Hover/WatchlistStatus'
import { InfoboxProps, Infobox } from './Hover/Infobox'
import { Icon, IconType } from '@amzn/dvui/dist/Icon'
import { Badge, badges } from '@amzn/dvui/dist/Badge'
import { Hover as BasicHover } from '@amzn/dvui/dist/Hover'
import { CommonPlaybackAction } from './Hover/Headline/PlaybackBlock'
import { Tooltip } from './Hover/Tooltip'
import { Summary } from './Hover/Summary'
import { Headline } from './Hover/Headline'
import PrerollPlayer, { PrerollPlayerPlacement } from '../DVWebNodeStorefront/shared/components/preroll/PrerollPlayer'
import { CustomerReviews } from '../../util/types/wps/hover'
import { VideoContext } from '../DVWebNodeStorefront/shared/utils/videoContext'
import { HideThisIcon } from './Hover/HideThis/container/HideThisIcon'
import { HideThisItemContainer } from "./Hover/HideThis/container/HideThisItemContainer"
import { PlayerContext } from '../../shared/player/integration/playbackArea'
import { ExtraMetadata } from './Hover/ExtraMetadata'
import { extractTitleIDTypeFromWatchlistAction, extractTitleIdFromWatchlistAction, createPlaybackAction, stringifyEnrichmentsOnClient } from './utils'

const METRIC_PREFIX = 'DVWeb::Hover2019::'
const METRICS = {
    enrichAttempt: METRIC_PREFIX + 'Enrich::Attempt',
    enrichAttemptInitial: METRIC_PREFIX + 'Enrich::Attempt::Initial',
    enrichComplete: METRIC_PREFIX + 'Enrich::Complete',
    enrichCompleteInitial: METRIC_PREFIX + 'Enrich::Complete::Initial',
    hoverNoEnrich: METRIC_PREFIX + 'Hover::NoEnrich',
    hover: METRIC_PREFIX + 'Hover',
    playerOpened: METRIC_PREFIX + 'Playback'
}

export interface UpdateableHoverProps {
    title?: string
    titleID?: string
    titleIDType?: "EPISODE" | "SEASON" | "MOVIE" | "CHANNEL" | "LIVEEVENT" | "UNKNOWN"
    synopsis?: string | DVString
    hasSubtitles?: boolean
    watchlistAction?: WatchlistAction
    liveInfo?: StorefrontItemLive
    liveBadgeLabel?: string
    playbackAction?: CommonPlaybackAction
    contentMaturityRating?: LinkableMaturityRatingProps
    imdbRating?: string
    releaseYear?: string | number
    runtime?: string
    trailer?: TrailerFragment
    offers?: DVString[]
    hideThisAction?: HideThisAction
    hideThisOutput?: HideThisOutput
    infoboxes?: InfoboxProps[]
    customerReviews?: CustomerReviews
    prerolls?: ItemPrerolls
    url?: string
    errored?: boolean
}

interface HoverProps extends UpdateableHoverProps {
    className?: string
    direction?: "up" | "down"
    disabled?: boolean
    children: React.ReactChild | React.ReactChild[]
    placeholderClassName?: string
    enrichTrailer?: boolean
    showPrerollsInHover?: boolean
    onMouseOver?: (event: React.MouseEvent<HTMLDivElement>) => void
    onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void
    onWatchlistToggle?: (newAction: WatchlistAction) => void
    removeItem?: () => void
}

declare let window: Window & {
    P: any
}

export interface PlayerCloseOpt {
    //This is incomplete, and declares only the parts needed for our current use case.
    videoType: string
    asin: string
    trailer: boolean
}

const TrailerWrapper = ({ trailer, children, }: { trailer: TrailerFragment, children: any }) => (
    <PlayerContext.Consumer>
        {({ onPlay }) => {
            const onClick = onPlay && ((e: React.MouseEvent) => {
                onPlay({
                    playbackUrl: trailer.playbackURL,
                    contentConfig: {
                        asin: trailer.playbackID,
                        videoType: trailer.videoMaterialType,
                        trailer: true,
                    },
                })
                e.preventDefault()
            })
            const button = <a
                className={cn(
                    { 'js-deeplinkable': !onClick },
                    'tst-trailer-button'
                )}
                href={trailer.playbackURL}
                data-page-title-id={trailer.playbackID}
                data-video-type={trailer.videoMaterialType}
                data-use-full-view='true'
                data-is-trailer='true'
                data-title-id={trailer.playbackID}
                data-ux-click='true'
                onClick={onClick || undefined}>
                {children}
            </a>
            if (!trailer.text)
                return button
            return (
                <Tooltip text={trailer.text}>
                    {button}
                </Tooltip>
            )
        }}
    </PlayerContext.Consumer>
)

const IconWrapper = ({ children }: { children: any }) => (
    <div className={cn(styles.iconWrapper, styles.alignBottom, styles.spaceRight)}>
        {children}
    </div>
)

class HoverComponent extends React.Component<HoverProps & Args, UpdateableHoverProps> {
    enrichmentInProgress?: boolean
    initialEnrichmentComplete?: boolean
    canEnrichTrailer: boolean
    prerollComponent?: PrerollPlayer
    expanded?: boolean
    onHideThisAction: (out: HideThisOutput) => void

    constructor(props: HoverProps & Args) {
        super(props)
        this.state = {
            ...props,
            titleIDType: props.titleIDType || extractTitleIDTypeFromWatchlistAction(props.watchlistAction),
            titleID: props.titleID || extractTitleIdFromWatchlistAction(props.watchlistAction)
        }
        this.canEnrichTrailer = !Boolean(this.props.trailer)
        this.onHideThisAction = this.onHideThisOutput.bind(this)
    }

    onHideThisOutput(out: HideThisOutput) {
        if (out.hideThisAction.tag === HideThisTag.HIDE) {
            this.setState({
                hideThisAction: out.hideThisAction,
                hideThisOutput: out
            })
        } else {
            this.onLeave()
            this.setState({
                hideThisOutput: out
            })

        }
    }

    isThisTitle(comparisonID: string) {
        const isScheduledId = this.state.titleID && this.state.titleID == comparisonID
        const isPlaybackActionId = this.state.playbackAction &&
            this.state.playbackAction.titleID &&
            this.state.playbackAction.titleID == comparisonID
        return isScheduledId || isPlaybackActionId
    }

    enrichMe(fetchTrailer: boolean, fetchPlayback: boolean, setEnrichedOnce?: boolean, fetchWatchlist?: boolean): Promise<void> {
        if (this.enrichmentInProgress)
            return Promise.resolve()
        this.enrichmentInProgress = true
        const titleID = this.state.titleID

        if (titleID && this.state.titleIDType != "CHANNEL") {
            const enrichmentRequest = this.createEnrichmentRequest(fetchTrailer, fetchPlayback, Boolean(fetchWatchlist))
            const wps = new WPSClient()
            this.props.logger.count(METRICS.enrichAttempt, 1, false)
            if (setEnrichedOnce)
                this.props.logger.count(METRICS.enrichAttemptInitial, 1, false)
            if (!enrichmentRequest)
                return Promise.resolve()
            return wps.EnrichItemMetadata({
                context: this.props.context,
                itemsToEnrich: stringifyEnrichmentsOnClient([enrichmentRequest])
            }).then(response => {
                this.props.logger.count(METRICS.enrichComplete, 1, false)
                this.applyEnrichmentResponse(response, fetchPlayback, fetchWatchlist)
                if (setEnrichedOnce) {
                    this.props.logger.count(METRICS.enrichCompleteInitial, 1, false)
                    this.initialEnrichmentComplete = true
                }
                if (fetchTrailer && this.canEnrichTrailer)
                    this.canEnrichTrailer = false
                this.enrichmentInProgress = false
            }
            ).catch(error => {
                this.props.logger.error(error)
                this.enrichmentInProgress = false
            })
        } else return Promise.resolve()
    }

    applyEnrichmentResponse(response: EnrichItemMetadataOutput, updatePlayback?: boolean, updateWatchlist?: boolean) {
        const titleID = this.state.titleID
        if (titleID && response.enrichments && response.enrichments[titleID]) {

            const enrichment = response.enrichments[titleID]

            this.setState(
                {
                    trailer: enrichment.trailer || this.state.trailer,
                    playbackAction: updatePlayback ? createPlaybackAction(enrichment) : this.state.playbackAction,
                    offers: (enrichment.messages && enrichment.messages.offers && enrichment.messages.offers.length) ? enrichment.messages.offers : this.state.offers,
                    prerolls: enrichment.prerolls || this.state.prerolls,
                    watchlistAction: updateWatchlist ? enrichment.watchlistAction : this.state.watchlistAction,
                    infoboxes: enrichment.messages && enrichment.messages.infoboxes
                }
            )
        }
    }

    createEnrichmentRequest(fetchTrailer: boolean, fetchPlayback: boolean, fetchWatchlist: boolean) {
        //[PVWebXPL-803] Deprecate this
        const shouldCallCcts = this.state.titleIDType &&
            (this.state.titleIDType == "SEASON" || this.state.titleIDType == "LIVEEVENT")
        const titleID = this.state.titleID
        const isLive = Boolean(this.state.liveBadgeLabel || this.state.liveInfo)
        if (!titleID)
            return


        return {
            titleID: titleID,
            titlePlayable: !shouldCallCcts,
            trailer: fetchTrailer,
            playback: fetchPlayback,
            preroll: this.props.showPrerollsInHover && !Boolean(this.props.prerolls),
            watchlist: fetchWatchlist,
            ...(isLive && { live: isLive })
        }

    }

    initialEnrich(): Promise<void> {
        if (!this.initialEnrichmentComplete)
            return this.enrichMe(this.canEnrichTrailer, true, true, true)
        else {
            this.props.logger.count(METRICS.hoverNoEnrich, 1, false)
            return Promise.resolve()
        }
    }

    onHover(ev: React.MouseEvent<HTMLDivElement>) {
        this.expanded = true
        if (this.props.onMouseOver)
            this.props.onMouseOver(ev)
        this.props.logger.count(METRICS.hover, 1, false)

        this.initialEnrich().then(_response => {
            this.expanded && this.prerollComponent && this.prerollComponent.startPlayer()
        })
    }

    onLeave(ev?: React.MouseEvent<HTMLDivElement>) {
        this.expanded = false
        if (this.props.onMouseLeave)
            this.props.onMouseLeave(ev)

        this.prerollComponent && this.prerollComponent.stop()
    }

    onFocus() {
        this.expanded = true
        this.initialEnrich()
    }

    onBlur() {
        this.expanded = false
    }

    componentDidMount() {
        if (window && window.P) {
            window.P.when('dv-player-integration').execute((player: any) => {
                player.on('play', (opt: PlayerCloseOpt) => {

                    const playingTitle = opt.asin
                    const isPlayingCurrentItem = this.isThisTitle(playingTitle)
                    if (isPlayingCurrentItem) {
                        const fetchTrailer = true
                        const fetchPlayback = true
                        this.props.logger.count(METRICS.playerOpened, 1, false)
                        player.on('closed', () => this.enrichMe(fetchTrailer, fetchPlayback)
                        )
                    }
                })
            }
            )
        }
    }

    componentDidCatch() {
        this.setState({ errored: true })
    }

    onWatchlistChange = (newAction: WatchlistAction) => {
        if (this.props.onWatchlistToggle)
            this.props.onWatchlistToggle(newAction)
        this.setState({ watchlistAction: newAction })
    }

    render() {
        if (this.state.errored || this.props.disabled)
            return this.props.children
        const watchlistAction = this.state.watchlistAction &&
            { ...this.state.watchlistAction, text: this.state.watchlistAction.text as DVString }

        const liveLabel = (this.state.liveBadgeLabel || (this.state.liveInfo &&
            this.state.liveInfo.status == "LIVE" &&
            this.state.liveInfo.label))
        const preFirstLine = liveLabel &&
            <div className={styles.preFirstLine}>
                <Badge className={styles.tailContent}
                    use={badges.LIVE}>
                    {liveLabel}
                </Badge>
            </div>

        const mainactions = <>
            {preFirstLine}
            <div className={styles.firstLine}>
                <Headline playbackAction={this.state.playbackAction}
                    offers={this.state.offers}
                    title={this.state.title} />
                <div className={styles.additionalIcons}>
                    {this.state.trailer &&
                        <IconWrapper>
                            <TrailerWrapper
                                trailer={this.state.trailer}>
                                <Icon use={IconType.TRAILER} className={cn(styles.tailContent, styles.alignBottom)} />
                            </TrailerWrapper>
                        </IconWrapper>}
                    {watchlistAction &&
                        <IconWrapper>
                            <WatchlistStatus
                                onToggle={this.onWatchlistChange}
                                watchlistAction={watchlistAction}
                                prerolls={this.props.prerolls}
                            />
                        </IconWrapper>}
                    {this.state.hideThisAction &&
                        <IconWrapper>
                            <HideThisIcon
                                {...this.state.hideThisAction}
                                onClick={this.onHideThisAction} />
                        </IconWrapper>
                    }
                </div>
            </div>
        </>


        const boxes = this.state.infoboxes &&
            this.state.infoboxes.map((infobox, index) => <Infobox {...infobox} key={index} />)

        const synopsis = <Summary title={this.state.title} synopsis={this.state.synopsis} />

        const extraMetadata = <ExtraMetadata {...this.state} />

        const blocks = [mainactions, boxes, synopsis, extraMetadata].filter(line => Boolean(line))
        return <HideThisItemContainer
            hideThisOutput={this.state.hideThisOutput}
            update={this.onHideThisAction}
            hideThis={() =>
                this.props.hideThisAction && Boolean(this.props.hideThisAction.hasTimer) && this.props.removeItem()}
            hasTimer={this.props.hideThisAction && safeBool(this.props.hideThisAction.hasTimer)}
            onMouseLeave={this.onLeave.bind(this)}
        >
            <BasicHover
                contentBlocks={blocks}
                className={cn(this.props.className, { [styles.forceUp]: this.props.direction == "up" }, 'tst-hover-container')}
                placeholderClassName={this.props.placeholderClassName}
                onMouseLeave={(ev) => this.onLeave(ev)}
                onMouseOver={(ev) => this.onHover(ev)}
                onFocusCapture={() => this.onFocus()}
                onBlurCapture={() => this.onBlur()}>
                <>
                    {this.props.children}
                    {Boolean(this.state.prerolls) &&
                        <VideoContext.Consumer>
                            {(context) =>
                                <PrerollPlayer
                                    prerolls={this.state.prerolls}
                                    placement={PrerollPlayerPlacement.Packshot}
                                    link={this.props.url}
                                    videoContext={context}
                                    playerIsActive={true}
                                    parentListeners={{
                                        onPlayStart: () => {
                                            if (!this.expanded && this.prerollComponent) {
                                                this.prerollComponent.stop()
                                            }
                                        }
                                    }}
                                    ref={(component) => this.prerollComponent = component}
                                />
                            }
                        </VideoContext.Consumer>
                    }
                </>
            </BasicHover>
        </HideThisItemContainer>
    }
}

export const Hover = withArgs(HoverComponent) as React.ComponentType<HoverProps>